// src/pages/admin/Building/EditBuilding.jsx
import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  FaArrowLeft,
  FaSave,
  FaTimes,
  FaSpinner,
  FaBuilding,
  FaMapMarkerAlt,
  FaMoneyBillWave,
  FaPhone,
  FaEnvelope,
  FaGlobe,
  FaUsers,
  FaBed,
  FaCheckCircle,
  FaStar,
  FaImage,
  FaTrash,
} from 'react-icons/fa';
import { successToast, errorToast } from '../../Utils/toast';
import {
  useGetAdminBuildingQuery,
  useUpdateBuildingMutation,
  useUploadBuildingImagesMutation,
  useDeleteBuildingImageMutation,
} from '../../redux/services/buildingService/building.Services';

function EditBuilding() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data, isLoading: isFetching } = useGetAdminBuildingQuery(id);
  const [updateBuilding, { isLoading: isUpdating }] = useUpdateBuildingMutation();
  const [uploadImages, { isLoading: isUploading }] = useUploadBuildingImagesMutation();
  const [deleteImage] = useDeleteBuildingImageMutation();

  const [formData, setFormData] = useState({
    name: '',
    type: 'samaj_bhawan',
    description: '',
    address: '',
    city: '',
    state: '',
    country: 'India',
    pincode: '',
    phone: '',
    email: '',
    website: '',
    capacity: '',
    rooms: '',
    facilities: '',
    price_per_day: '',
    security_deposit: '',
    is_featured: false,
    is_active: true,
    status: 'approved',
    meta_title: '',
    meta_description: '',
    meta_keywords: '',
  });

  const [images, setImages] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (data?.data) {
      const building = data.data;
      setFormData({
        name: building.name || '',
        type: building.type || 'samaj_bhawan',
        description: building.description || '',
        address: building.address || '',
        city: building.city || '',
        state: building.state || '',
        country: building.country || 'India',
        pincode: building.pincode || '',
        phone: building.phone || '',
        email: building.email || '',
        website: building.website || '',
        capacity: building.capacity || '',
        rooms: building.rooms || '',
        facilities: building.facilities || '',
        price_per_day: building.price_per_day || '',
        security_deposit: building.security_deposit || '',
        is_featured: building.is_featured === 1 || building.is_featured === true,
        is_active: building.is_active === 1 || building.is_active === true,
        status: building.status || 'approved',
        meta_title: building.meta_title || '',
        meta_description: building.meta_description || '',
        meta_keywords: building.meta_keywords || '',
      });
      setImages(building.images || []);
    }
  }, [data]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    setSelectedFiles([...selectedFiles, ...files]);
  };

  const removeSelectedFile = (index) => {
    const newFiles = [...selectedFiles];
    newFiles.splice(index, 1);
    setSelectedFiles(newFiles);
  };

  const handleUploadImages = async () => {
    if (selectedFiles.length === 0) {
      errorToast('Please select images to upload');
      return;
    }

    try {
      setUploading(true);
      const formDataToSend = new FormData();
      selectedFiles.forEach((file) => {
        formDataToSend.append('images', file);
      });

      await uploadImages({ id, data: formDataToSend }).unwrap();
      successToast('Images uploaded successfully');
      setSelectedFiles([]);
      // Refetch building data
      window.location.reload();
    } catch (error) {
      errorToast(error?.data?.message || 'Failed to upload images');
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteImage = async (imageId) => {
    if (!window.confirm('Are you sure you want to delete this image?')) {
      return;
    }

    try {
      await deleteImage(imageId).unwrap();
      successToast('Image deleted successfully');
      setImages(images.filter(img => img.id !== imageId));
    } catch (error) {
      errorToast(error?.data?.message || 'Failed to delete image');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      errorToast('Building name is required');
      return;
    }

    try {
      const response = await updateBuilding({ id, data: formData }).unwrap();
      successToast(response?.message || 'Building updated successfully');
      navigate('/admin/buildings');
    } catch (error) {
      console.error('Update error:', error);
      errorToast(error?.data?.message || 'Failed to update building');
    }
  };

  if (isFetching) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <h5 className="mt-3">Loading building details...</h5>
      </div>
    );
  }

  return (
    <div>
      {/* HEADER */}
      <div className="page-header-breadcrumb all_breadcrumb">
        <div className="ms-md-1 mb-1 mb-md-0 ms-0 page_heading_breadcrumb">
          <nav>
            <h4 className="fw-semibold">
              <Link to="/admin/buildings" className="text-primary me-2">
                <FaArrowLeft />
              </Link>
              <FaBuilding className="me-2 text-warning" />
              Edit Building
            </h4>
            <ol className="breadcrumb mb-0">
              <li className="breadcrumb-item active">
                <Link to="/admin">Home</Link>
              </li>
              <li className="breadcrumb-item">
                <Link to="/admin/buildings">Buildings</Link>
              </li>
              <li className="breadcrumb-item">Edit Building</li>
            </ol>
          </nav>
        </div>
        <div className="page-title fw-semibold fs-18 mb-0">
          <button
            className="btn btn-secondary me-2"
            onClick={() => navigate('/admin/buildings')}
          >
            <FaTimes className="me-1" /> Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={handleSubmit}
            disabled={isUpdating}
          >
            {isUpdating ? (
              <>
                <FaSpinner className="spinning me-2" />
                Updating...
              </>
            ) : (
              <>
                <FaSave className="me-1" /> Update Building
              </>
            )}
          </button>
        </div>
      </div>

      {/* FORM */}
      <div className="card">
        <div className="card-body">
          <form onSubmit={handleSubmit}>
            <div className="row">
              {/* Same fields as CreateBuilding */}
              <div className="col-12">
                <h5 className="fw-bold mb-3">📋 Basic Information</h5>
              </div>

              <div className="col-md-6">
                <div className="form-group mb-3">
                  <label>
                    Building Name <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Enter building name"
                    required
                  />
                </div>
              </div>

              <div className="col-md-3">
                <div className="form-group mb-3">
                  <label>Type</label>
                  <select
                    className="form-control"
                    name="type"
                    value={formData.type}
                    onChange={handleChange}
                  >
                    <option value="samaj_bhawan">Samaj Bhawan</option>
                    <option value="dharamshala">Dharamshala</option>
                    <option value="community_hall">Community Hall</option>
                    <option value="marriage_hall">Marriage Hall</option>
                  </select>
                </div>
              </div>

              <div className="col-md-3">
                <div className="form-group mb-3">
                  <label>Status</label>
                  <select
                    className="form-control"
                    name="status"
                    value={formData.status}
                    onChange={handleChange}
                  >
                    <option value="pending">Pending</option>
                    <option value="approved">Approved</option>
                    <option value="rejected">Rejected</option>
                  </select>
                </div>
              </div>

              <div className="col-12">
                <div className="form-group mb-3">
                  <label>Description</label>
                  <textarea
                    className="form-control"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    placeholder="Enter building description"
                    rows="4"
                  />
                </div>
              </div>

              {/* Location Information */}
              <div className="col-12 mt-3">
                <h5 className="fw-bold mb-3">
                  <FaMapMarkerAlt className="me-2" /> Location Information
                </h5>
              </div>

              <div className="col-md-6">
                <div className="form-group mb-3">
                  <label>Address</label>
                  <input
                    type="text"
                    className="form-control"
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    placeholder="Enter address"
                  />
                </div>
              </div>

              <div className="col-md-3">
                <div className="form-group mb-3">
                  <label>City</label>
                  <input
                    type="text"
                    className="form-control"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    placeholder="Enter city"
                  />
                </div>
              </div>

              <div className="col-md-3">
                <div className="form-group mb-3">
                  <label>State</label>
                  <input
                    type="text"
                    className="form-control"
                    name="state"
                    value={formData.state}
                    onChange={handleChange}
                    placeholder="Enter state"
                  />
                </div>
              </div>

              <div className="col-md-4">
                <div className="form-group mb-3">
                  <label>Country</label>
                  <input
                    type="text"
                    className="form-control"
                    name="country"
                    value={formData.country}
                    onChange={handleChange}
                    placeholder="Enter country"
                  />
                </div>
              </div>

              <div className="col-md-4">
                <div className="form-group mb-3">
                  <label>Pincode</label>
                  <input
                    type="text"
                    className="form-control"
                    name="pincode"
                    value={formData.pincode}
                    onChange={handleChange}
                    placeholder="Enter pincode"
                  />
                </div>
              </div>

              {/* Contact Information */}
              <div className="col-12 mt-3">
                <h5 className="fw-bold mb-3">
                  <FaPhone className="me-2" /> Contact Information
                </h5>
              </div>

              <div className="col-md-4">
                <div className="form-group mb-3">
                  <label>Phone</label>
                  <input
                    type="tel"
                    className="form-control"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="Enter phone number"
                  />
                </div>
              </div>

              <div className="col-md-4">
                <div className="form-group mb-3">
                  <label>Email</label>
                  <input
                    type="email"
                    className="form-control"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="Enter email address"
                  />
                </div>
              </div>

              <div className="col-md-4">
                <div className="form-group mb-3">
                  <label>Website</label>
                  <input
                    type="url"
                    className="form-control"
                    name="website"
                    value={formData.website}
                    onChange={handleChange}
                    placeholder="Enter website URL"
                  />
                </div>
              </div>

              {/* Building Details */}
              <div className="col-12 mt-3">
                <h5 className="fw-bold mb-3">
                  <FaBuilding className="me-2" /> Building Details
                </h5>
              </div>

              <div className="col-md-3">
                <div className="form-group mb-3">
                  <label>Capacity</label>
                  <input
                    type="number"
                    className="form-control"
                    name="capacity"
                    value={formData.capacity}
                    onChange={handleChange}
                    placeholder="Max capacity"
                    min="0"
                  />
                </div>
              </div>

              <div className="col-md-3">
                <div className="form-group mb-3">
                  <label>Rooms</label>
                  <input
                    type="number"
                    className="form-control"
                    name="rooms"
                    value={formData.rooms}
                    onChange={handleChange}
                    placeholder="Number of rooms"
                    min="0"
                  />
                </div>
              </div>

              <div className="col-md-6">
                <div className="form-group mb-3">
                  <label>Facilities</label>
                  <input
                    type="text"
                    className="form-control"
                    name="facilities"
                    value={formData.facilities}
                    onChange={handleChange}
                    placeholder="e.g., Parking, AC, WiFi, Kitchen"
                  />
                </div>
              </div>

              {/* Pricing */}
              <div className="col-12 mt-3">
                <h5 className="fw-bold mb-3">
                  <FaMoneyBillWave className="me-2" /> Pricing
                </h5>
              </div>

              <div className="col-md-4">
                <div className="form-group mb-3">
                  <label>Price Per Day (₹)</label>
                  <input
                    type="number"
                    className="form-control"
                    name="price_per_day"
                    value={formData.price_per_day}
                    onChange={handleChange}
                    placeholder="Enter price per day"
                    min="0"
                    step="100"
                  />
                </div>
              </div>

              <div className="col-md-4">
                <div className="form-group mb-3">
                  <label>Security Deposit (₹)</label>
                  <input
                    type="number"
                    className="form-control"
                    name="security_deposit"
                    value={formData.security_deposit}
                    onChange={handleChange}
                    placeholder="Enter security deposit"
                    min="0"
                    step="100"
                  />
                </div>
              </div>

              {/* Options */}
              <div className="col-12 mt-3">
                <h5 className="fw-bold mb-3">⚙️ Options</h5>
              </div>

              <div className="col-md-4">
                <div className="form-group mb-3">
                  <div className="form-check mt-2">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      name="is_featured"
                      checked={formData.is_featured}
                      onChange={handleChange}
                      id="is_featured"
                    />
                    <label className="form-check-label" htmlFor="is_featured">
                      <FaStar className="text-warning me-1" /> Featured Building
                    </label>
                  </div>
                </div>
              </div>

              <div className="col-md-4">
                <div className="form-group mb-3">
                  <div className="form-check mt-2">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      name="is_active"
                      checked={formData.is_active}
                      onChange={handleChange}
                      id="is_active"
                    />
                    <label className="form-check-label" htmlFor="is_active">
                      <FaCheckCircle className="text-success me-1" /> Active Building
                    </label>
                  </div>
                </div>
              </div>

              {/* Meta Information */}
              <div className="col-12 mt-3">
                <h5 className="fw-bold mb-3">🔍 SEO / Meta Information</h5>
              </div>

              <div className="col-12">
                <div className="form-group mb-3">
                  <label>Meta Title</label>
                  <input
                    type="text"
                    className="form-control"
                    name="meta_title"
                    value={formData.meta_title}
                    onChange={handleChange}
                    placeholder="Meta title for SEO"
                  />
                </div>
              </div>

              <div className="col-12">
                <div className="form-group mb-3">
                  <label>Meta Description</label>
                  <textarea
                    className="form-control"
                    name="meta_description"
                    value={formData.meta_description}
                    onChange={handleChange}
                    placeholder="Meta description for SEO"
                    rows="2"
                  />
                </div>
              </div>

              <div className="col-12">
                <div className="form-group mb-3">
                  <label>Meta Keywords</label>
                  <input
                    type="text"
                    className="form-control"
                    name="meta_keywords"
                    value={formData.meta_keywords}
                    onChange={handleChange}
                    placeholder="Comma separated keywords"
                  />
                </div>
              </div>

              {/* Image Upload Section */}
              <div className="col-12 mt-4">
                <h5 className="fw-bold mb-3">
                  <FaImage className="me-2" /> Building Images
                </h5>
              </div>

              {/* Existing Images */}
              {images.length > 0 && (
                <div className="col-12 mb-3">
                  <div className="row g-2">
                    {images.map((image) => (
                      <div key={image.id} className="col-md-3 col-4">
                        <div className="position-relative">
                          <img
                            src={image.image_url}
                            alt={image.caption || 'Building'}
                            className="img-fluid rounded"
                            style={{ height: '120px', width: '100%', objectFit: 'cover' }}
                          />
                          {image.is_primary && (
                            <span className="position-absolute top-0 start-0 badge bg-primary m-1">
                              Primary
                            </span>
                          )}
                          <button
                            type="button"
                            className="btn btn-danger btn-sm position-absolute top-0 end-0 m-1"
                            onClick={() => handleDeleteImage(image.id)}
                          >
                            <FaTrash />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Upload New Images */}
              <div className="col-12">
                <div className="form-group mb-3">
                  <label>Upload New Images</label>
                  <input
                    type="file"
                    className="form-control"
                    onChange={handleFileChange}
                    multiple
                    accept="image/*"
                  />
                  <small className="text-muted">You can select multiple images</small>
                </div>

                {selectedFiles.length > 0 && (
                  <div className="mb-3">
                    <div className="d-flex flex-wrap gap-2">
                      {selectedFiles.map((file, index) => (
                        <div key={index} className="badge bg-primary d-flex align-items-center gap-2 p-2">
                          <span>{file.name}</span>
                          <button
                            type="button"
                            className="btn btn-link btn-sm text-white p-0"
                            onClick={() => removeSelectedFile(index)}
                          >
                            <FaTimes />
                          </button>
                        </div>
                      ))}
                    </div>
                    <button
                      type="button"
                      className="btn btn-success mt-2"
                      onClick={handleUploadImages}
                      disabled={isUploading || uploading}
                    >
                      {isUploading || uploading ? (
                        <>
                          <FaSpinner className="spinning me-2" />
                          Uploading...
                        </>
                      ) : (
                        <>
                          <FaImage className="me-2" />
                          Upload {selectedFiles.length} Images
                        </>
                      )}
                    </button>
                  </div>
                )}
              </div>

              {/* Submit Button */}
              <div className="col-12 mt-4">
                <button
                  type="submit"
                  className="btn btn-primary btn-lg w-100"
                  disabled={isUpdating}
                >
                  {isUpdating ? (
                    <>
                      <FaSpinner className="spinning me-2" />
                      Updating Building...
                    </>
                  ) : (
                    <>
                      <FaSave className="me-2" />
                      Update Building
                    </>
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default EditBuilding;